/**
 * Transaction process graph for bookings with approval:
 *   - default-booking-with-approval
 *
 * Flow: Customer requests → Host approves → Customer pays
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking request, a transaction is created
  // At this transition a PaymentIntent is created but NOT confirmed yet
  REQUEST_BOOKING: 'transition/request-booking',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a booking request.
  INQUIRE: 'transition/inquire',
  REQUEST_BOOKING_AFTER_INQUIRY: 'transition/request-booking-after-inquiry',

  // Host accepts or declines the booking request
  ACCEPT_BOOKING: 'transition/accept-booking',
  DECLINE_BOOKING: 'transition/decline-booking',

  // If host doesn't respond in 48 hours, the request expires
  EXPIRE_BOOKING_REQUEST: 'transition/expire-booking-request',

  // After host accepts, customer confirms the payment
  // This is when the PaymentIntent is actually confirmed and captured
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Booking completion (at booking end time)
  COMPLETE_BOOKING: 'transition/complete-booking',

  // Auto-complete after booking ends
  AUTO_COMPLETE: 'transition/auto-complete',

  // Cancellations
  CANCEL: 'transition/cancel',
  OPERATOR_CANCEL: 'transition/operator-cancel',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_APPROVAL: 'pending-approval',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  CANCELED: 'canceled',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  REVIEWED_BY_BOTH: 'reviewed-by-both',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking-with-approval/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_BOOKING]: states.PENDING_APPROVAL,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_BOOKING_AFTER_INQUIRY]: states.PENDING_APPROVAL,
      },
    },

    [states.PENDING_APPROVAL]: {
      on: {
        [transitions.ACCEPT_BOOKING]: states.ACCEPTED,
        [transitions.DECLINE_BOOKING]: states.DECLINED,
        [transitions.EXPIRE_BOOKING_REQUEST]: states.DECLINED,
      },
    },

    [states.ACCEPTED]: {
      on: {
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
      },
    },

    [states.DECLINED]: {},
    [states.PAYMENT_EXPIRED]: {},

    [states.PREAUTHORIZED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.OPERATOR_CANCEL]: states.CANCELED,
        [transitions.COMPLETE_BOOKING]: states.DELIVERED,
      },
    },

    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED_BY_BOTH,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED_BY_BOTH,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED_BY_BOTH,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED_BY_BOTH,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED_BY_BOTH,
      },
    },
    [states.REVIEWED_BY_BOTH]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT_BOOKING,
    transitions.DECLINE_BOOKING,
    transitions.CANCEL,
    transitions.OPERATOR_CANCEL,
    transitions.COMPLETE_BOOKING,
    transitions.AUTO_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_BOOKING,
    transitions.REQUEST_BOOKING_AFTER_INQUIRY,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE_BOOKING,
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.DECLINE_BOOKING,
    transitions.EXPIRE_BOOKING_REQUEST,
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.OPERATOR_CANCEL,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PENDING_APPROVAL];
